import PieInfoWidget from '../Widgets/PieInfoWidgetPrimary';
import { Box, Grid, Typography } from '@mui/material';
import LoaderComponent from 'components/Loader';
import { useState, memo, useEffect, useMemo } from 'react';
import { formatNumber } from './utils/pipelineChartUtils';

interface RunDistributionProps {
  data?: TransformedRunDistributionData;
  isLoading?: boolean;
  onSliceClick?: (source: string) => void;
}

export interface RunDistributionItem {
  title: string;
  data: Array<{
    name: string;
    value: number;
    percentage: string;
    displayName?: string;
    formattedValue?: string;
  }>;
}

export interface TransformedRunDistributionData {
  result: RunDistributionItem[];
}

const RunDistributionComponent: React.FC<RunDistributionProps> = ({
  data,
  isLoading,
  onSliceClick,
}) => {
  const [expandedTitle, setExpandedTitle] = useState<string>('');

  useEffect(() => {
    if (data?.result?.[0]?.title && !expandedTitle) {
      setExpandedTitle(data.result[0].title);
    }
  }, [data, expandedTitle]);

  const charts: RunDistributionItem[] = data?.result ?? [];
  const expandedChart = charts.find((c) => c.title === expandedTitle) || charts[0];

  // Transform data for RunDistribution: truncate names and format values
  const transformSeriesDataForDisplay = (
    items: RunDistributionItem['data'],
  ): RunDistributionItem['data'] =>
    items
      .filter((item) => !(item.value === 0 && item.percentage === '0.000%'))
      .map((item) => ({
        ...item,
        displayName: item.name.length > 10 ? `${item.name.substring(0, 10)}...` : item.name,
        formattedValue: formatNumber(item.value),
        _pct: Number.parseFloat(String(item.percentage).replace('%', '')) || 0,
      }))
      .sort((a, b) => {
        if (b._pct !== a._pct) return b._pct - a._pct;
        if (b.value !== a.value) return b.value - a.value;
        return a.name.localeCompare(b.name);
      })
      .map(({ _pct, ...rest }) => rest);

  const transformedExpandedData = transformSeriesDataForDisplay(expandedChart?.data ?? []);

  const transformedChartsData = useMemo(
    () =>
      charts.map((chart) => ({
        ...chart,
        data: transformSeriesDataForDisplay(chart.data),
      })),
    [charts],
  );

  if (!charts?.length && !isLoading) {
    return null;
  }

  return (
    <Grid container sx={{ mt: 0 }}>
      <Grid item xs={12} md={8}>
        <Box sx={{ width: '100%' }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 500,
              fontSize: '30px',
              mb: 1,
              color: '#003771',
              pl: 0,
            }}
          >
            Run Distributions
          </Typography>

          <Box sx={{ minHeight: 400, position: 'relative' }}>
            {isLoading ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LoaderComponent />
              </Box>
            ) : (
              <PieInfoWidget
                title={expandedChart?.title}
                value={0}
                seriesData={transformedExpandedData}
                isLoading={false}
                isExpanded={true}
                hasExpand={false}
                variant="distribution"
                labelFontSize={16}
                wrapperSxOverride={{
                  padding: 0,
                }}
                isRunDistribution={true}
              />
            )}
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={4}>
        <Grid
          container
          spacing={2}
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: 2,
            height: '100%',
            padding: '12px 8px',
          }}
        >
          {isLoading ? (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <LoaderComponent />
            </Grid>
          ) : (
            transformedChartsData.map((chart: RunDistributionItem) => {
              if (chart.title === expandedTitle) return null;

              return (
                <Grid item xs={12} key={chart.title} sx={{ px: 1 }}>
                  <Box
                    sx={{
                      backgroundColor: '#EFF6FF',
                      borderRadius: 2,
                      padding: 2,
                    }}
                  >
                    <PieInfoWidget
                      title={chart.title}
                      value={0}
                      seriesData={chart.data}
                      isLoading={false}
                      isExpanded={false}
                      hasExpand={false}
                      variant="distribution"
                      customSize="160px"
                      sxOverride={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                      }}
                      wrapperSxOverride={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        padding: 0,
                      }}
                      onToggleExpand={() => setExpandedTitle(chart.title)}
                      isRunDistribution={true}
                    />
                  </Box>
                </Grid>
              );
            })
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
const RunDistribution = memo(RunDistributionComponent);

export default RunDistribution;
